import { computed, inject, onMounted, ref, type Ref } from 'vue';
import { useRouter } from 'vue-router';

import { useLoader } from '@flash-global66/g-loader';
import { toast } from '@flash-global66/g-toast';

import { formatByteStringForBlob, manageBlobDownload } from '@/helpers/files';
import type { ActionsStepGrid } from '@/types/ui/StepperTypes';
import type { RouteProductType } from '@/types/RouteTransferType';
import type { CountryData } from '@/types/CountryType';

import { transactionService, countryService } from '@/shared/common';
import { useAccountStore } from '@/stores/account';

import {
  BANK_NAME_ICON_MAP,
  STABLECOIN_CURRENCY_ICON_MAP,
} from '@/shared/common/constants/stablecoinIcons.constant';
import { getAsset } from '@/helpers/assets';

import { transactionProviderKey as transactionProvider,
  initialValuesTransactionProvider,
} from '@/modules/transaction/constants/transactionProvider.constant';

import type { NewTypeMovement } from '@/shared/common/types/movement.type';
import type { IP2pDetail } from '@/types/InterfaceP2pDetail';
import {
  buildDefaultInvoicePayload,
  buildP2PVoucherValues,
  buildP2pRemittanceInvoicePayload,
} from '@/modules/movements/utils/buildVouchersPayload.util';
import { DOWNLOAD_TEMPLATES } from '@/modules/movements/constants/downloadTemplates.constant';
import {
  isVirtualAccountTransaction,
  resolveVirtualAccountVoucherTemplate,
} from '@/modules/movements/utils/virtualAccountMovement.util';
import { useVirtualAccountsB2b } from '@/shared/firebase';

import { isCcaAmountLimitValid } from '@/modules/transaction/utils/cca.util';
import { useBrebSendContext } from '@/modules/transaction/composables/useBrebSendContext';
import { useBrebSigner } from '@/modules/transaction/composables/useBrebSigner';
import type { TransactionPayload } from '@/types/TransactionType';
import type { TypeTransactionPurpose } from '@/types/TypeTransactionPurpose';
import type { PurposeComexInfo } from '@/modules/transaction/types/comex.type';
import type { SuccessTransactionProps } from '../../types/successTransaction.type';

export function useSuccessTransaction(props: SuccessTransactionProps) {
  const {
    preTransactionResponse,
    quoteData,
    isCCA,
    selectedContact,
    selectedContactP2P,
    isBrazilPix,
    isComexFlow,
    purposeSelected,
    selectedPurposesComex,
    transactionPayload,
    isP2P,
    isStablecoin,
    navigateTo,
  } = inject(transactionProvider, initialValuesTransactionProvider);

  const router = useRouter();
  const { showGLoader } = useLoader();
  const { kycStatus } = useAccountStore();
  const { isEnabled: isVirtualAccountsEnabled } = useVirtualAccountsB2b();
  const { isBrebSendContext } = useBrebSendContext();

  const { fetchBrebSigner, beneficiaryBank, beneficiaryAccountNumber } =
    useBrebSigner(selectedContact, isBrebSendContext);

  const countries: Ref<CountryData[]> = ref([]);

  const originCountry = computed(() => kycStatus.company.addressCountry.country);

  const walletAddress = computed(() =>
    isStablecoin.value ? (selectedContact.value?.accountNumber || '') : ''
  );

  const networkName = computed(() =>
    isStablecoin.value ? (selectedContact.value?.accountBankName || '') : ''
  );

  const networkIcon = computed(() => {
    if (!isStablecoin.value) return '';
    const path = BANK_NAME_ICON_MAP[networkName.value];
    return path ? getAsset(path) : '';
  });

  const currencyIcon = computed(() => {
    if (!isStablecoin.value) return '';
    const path = STABLECOIN_CURRENCY_ICON_MAP[(quoteData as Ref).value?.destinationCurrency ?? ''];
    return path ? getAsset(path) : '';
  });

  const currencyProductType = computed((): RouteProductType => {
    return props.successType === 'P2P' || props.successType === 'P2P_COMEX'
      ? 'P2P'
      : 'REMITTANCE';
  });

  const slaImmediate = computed(() => {
    const amount = Number(quoteData.value?.originAmount) || 0;

    if (typeof preTransactionResponse.value.expiryDate !== 'string') return true;
    if (isP2P.value || isBrebSendContext.value) return true;

    return isCcaAmountLimitValid(isCCA.value, amount);
  });

  const title = computed(() => {
    // En Bre-B el envío es inmediato: siempre se confirma como realizado (BB-258).
    if (isBrebSendContext.value) {
      return 'El envío fue realizado correctamente';
    }
    if (slaImmediate.value) {
      return 'Tu envío fue realizado correctamente';
    }
    return 'Tu transacción está siendo procesada';
  });

  const actions = computed<ActionsStepGrid>(() => ({
    back: {
      label: '',
      function: () => {},
      hide: true,
    },
    next: {
      label: 'Entendido',
      function: goToMovements,
    },
  }));

  const canDownloadVoucher = computed(() => !isStablecoin.value);

  const displayedTransactionId = computed(() =>
    preTransactionResponse.value.p2pRemittance
      ? preTransactionResponse.value.p2pTransactionId
      : preTransactionResponse.value.id
  );

  function goToTransaction() {
    purposeSelected.value = {} as TypeTransactionPurpose;
    selectedPurposesComex.value = {} as PurposeComexInfo;
    transactionPayload.value = {} as TransactionPayload;
    navigateTo('type-transfer');
  }

  function goToMovements() {
    router.push({ name: 'movements' });
  }

  /**
   * Navega al flujo de envío según el tipo (P2P o TRANSACTION), replicando el
   * comportamiento de las product cards originales (redirect + reload). Se usa
   * en las cards Bre-B "Ir a Enviar" para conservar destinos distintos.
   */
  async function goToSend(type: 'P2P' | 'TRANSACTION') {
    await router.push({ name: 'transaction', query: { type } });
    location.reload();
  }

  async function downloadP2PVoucher() {
    const transactionId = preTransactionResponse.value.id;

    const detailResult = await transactionService.getP2pDetail({ transactionId });
    await detailResult.foldAsync(
      async (failure) => {
        toast.error(failure.message || 'Error al descargar el comprobante');
      },
      async (detailSuccess) => {
        const p2pDetail = detailSuccess.data as IP2pDetail;
        const isVirtualAccountTx = isVirtualAccountTransaction(
          p2pDetail.comment,
          isVirtualAccountsEnabled.value
        );

        const values = buildP2PVoucherValues({
          movement: { type: 'P2P_SENT' } as unknown as NewTypeMovement,
          p2pDetail,
          countries: countries.value,
          kycStatus,
          hideComment: isVirtualAccountTx,
        });

        const template = resolveVirtualAccountVoucherTemplate(
          DOWNLOAD_TEMPLATES.p2p,
          originCountry.value,
          isVirtualAccountTx
        );

        if (!template) {
          toast.error('No se ha encontrado la plantilla del comprobante');
          return;
        }

        const voucherResult = await transactionService.downloadVoucher({ template, values });
        voucherResult.fold(
          () => { toast.error('Error al descargar el comprobante'); },
          (voucherSuccess) => {
            const documentBytes = voucherSuccess.data?.documentBytes;
            if (!documentBytes) {
              toast.error('No se encontraron datos del comprobante');
              return;
            }
            const fileData = formatByteStringForBlob(documentBytes);
            manageBlobDownload(fileData as BlobPart, `comprobante_p2p_${transactionId}`);
          }
        );
      }
    );
  }

  async function downloadRemittanceVoucher() {
    const transactionId = preTransactionResponse.value.id;

    const result = await transactionService.getTransaction({
      transactionId,
      productType: 'REMITTANCE',
    });
    await result.foldAsync(
      async (failure) => {
        toast.error(failure.message || 'Error al descargar el comprobante');
      },
      async (success) => {
        const payload = buildDefaultInvoicePayload(success.data);
        const downloadResult = await transactionService.downloadInvoice(payload);
        downloadResult.fold(
          (failure) => { toast.error(failure.message || 'Error al descargar el comprobante'); },
          (downloadSuccess) => {
            if (!downloadSuccess.data) {
              toast.error('Error al descargar el comprobante: datos no disponibles');
              return;
            }
            manageBlobDownload(downloadSuccess.data, String(transactionId));
          }
        );
      }
    );
  }

  async function downloadP2pRemittanceVoucher() {
    const transactionId = preTransactionResponse.value.p2pTransactionId;
    if (!transactionId) {
      toast.error('Error al descargar el comprobante: datos no disponibles');
      return;
    }

    const result = await transactionService.getP2pRemittance(transactionId);
    await result.foldAsync(
      async (failure) => {
        toast.error(failure.message || 'Error al descargar el comprobante');
      },
      async (success) => {
        const payload = buildP2pRemittanceInvoicePayload(success.data);
        const downloadResult = await transactionService.downloadInvoice(payload);
        downloadResult.fold(
          (failure) => {
            toast.error(failure.message || 'Error al descargar el comprobante');
          },
          (downloadSuccess) => {
            if (!downloadSuccess.data) {
              toast.error('Error al descargar el comprobante: datos no disponibles');
              return;
            }
            manageBlobDownload(downloadSuccess.data, String(transactionId));
          }
        );
      }
    );
  }

  async function downloadVoucher() {
    showGLoader(true);
    try {
      if (preTransactionResponse.value.p2pRemittance) {
        await downloadP2pRemittanceVoucher();
      } else if (isP2P.value) {
        await downloadP2PVoucher();
      } else {
        await downloadRemittanceVoucher();
      }
    } finally {
      showGLoader(false);
    }
  }

  async function loadCountries() {
    if (countries.value.length) return;
    const result = await countryService.getCountries();
    if (result.isRight()) {
      countries.value = result.value.data;
    }
  }

  onMounted(() => {
    loadCountries();
    fetchBrebSigner();
  });

  return {
    preTransactionResponse,
    quoteData,
    isBrazilPix,
    isComexFlow,
    purposeSelected,
    selectedPurposesComex,
    transactionPayload,
    isP2P,
    isStablecoin,
    selectedContact,
    selectedContactP2P,
    beneficiaryBank,
    beneficiaryAccountNumber,
    walletAddress,
    networkName,
    networkIcon,
    currencyIcon,
    currencyProductType,
    slaImmediate,
    title,
    actions,
    canDownloadVoucher,
    displayedTransactionId,
    downloadVoucher,
    goToTransaction,
    goToMovements,
    goToSend,
  };
}
